import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Globe, Search } from 'lucide-react';
import { fastapiService } from '../../services/fastapiService';
import { ENTERPRISE_ADMIN_BASE } from '../../config/adminLayers';

type DomainRow = {
  id: string;
  workspace_id?: string;
  domain_name?: string;
  provider_type?: string;
  verification_status?: string;
  is_active?: boolean;
  is_default?: boolean;
  workspace?: { id: string; name?: string } | null;
};

/**
 * Cross-tenant verified sending domains — uses GET /admin/verified-domains (additive).
 */
export const EnterpriseVerifiedDomainsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState<DomainRow[]>([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const totalPages = Math.max(1, Math.ceil(total / limit));

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const params: Record<string, string | number> = { page, limit };
        if (search.trim()) params.search = search.trim();
        if (statusFilter.trim()) params.verification_status = statusFilter.trim();
        const res = await fastapiService.getAdminVerifiedDomains(params);
        if (cancelled) return;
        setRows((res.data as DomainRow[]) || []);
        setTotal(res.pagination?.total ?? 0);
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load domains');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [page, limit, search, statusFilter]);

  const applySearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-100 text-emerald-800">
            <Globe className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-enterprise-900">Sending domains</h2>
            <p className="mt-1 text-sm text-enterprise-600">
              Cross-tenant verified domains for email sending — drill into a workspace for full 360° context.
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-enterprise-200 bg-white p-4 shadow-sm">
        <form onSubmit={applySearch} className="flex flex-wrap items-end gap-3">
          <div className="min-w-[200px] flex-1">
            <label htmlFor="domain-search" className="block text-xs font-medium text-enterprise-500">
              Search domain
            </label>
            <div className="relative mt-1">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-enterprise-400" />
              <input
                id="domain-search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full rounded-lg border border-enterprise-200 py-2 pl-9 pr-3 text-sm text-enterprise-900 outline-none focus:border-emerald-500"
                placeholder="mail.example.com"
              />
            </div>
          </div>
          <div>
            <label htmlFor="vstatus" className="block text-xs font-medium text-enterprise-500">
              Status
            </label>
            <input
              id="vstatus"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="mt-1 w-40 rounded-lg border border-enterprise-200 px-3 py-2 text-sm text-enterprise-900 outline-none focus:border-emerald-500"
              placeholder="e.g. verified"
            />
          </div>
          <button
            type="submit"
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"
          >
            Search
          </button>
        </form>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {loading ? (
        <p className="py-12 text-center text-enterprise-500">Loading domains…</p>
      ) : (
        <>
          <div className="overflow-hidden rounded-xl border border-enterprise-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-enterprise-100 text-left text-sm">
              <thead className="bg-enterprise-50 text-xs font-semibold uppercase tracking-wide text-enterprise-500">
                <tr>
                  <th className="px-4 py-3">Domain</th>
                  <th className="px-4 py-3">Workspace</th>
                  <th className="px-4 py-3">Provider</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Flags</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-enterprise-100">
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-enterprise-500">
                      No domains found.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => {
                        const wid = r.workspace_id ? String(r.workspace_id) : '';
                        return (
                          <tr key={r.id} className="hover:bg-enterprise-50/80">
                            <td className="px-4 py-3 font-medium text-enterprise-900">{r.domain_name || '—'}</td>
                            <td className="px-4 py-3">
                              {wid ? (
                                <Link
                                  to={`${ENTERPRISE_ADMIN_BASE}/workspaces/${wid}`}
                                  className="font-medium text-emerald-700 hover:underline"
                                >
                                  {r.workspace?.name || wid.slice(0, 8) + '…'}
                                </Link>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td className="px-4 py-3 text-enterprise-600">{r.provider_type || '—'}</td>
                            <td className="px-4 py-3 text-enterprise-600">{r.verification_status || '—'}</td>
                            <td className="px-4 py-3 text-xs text-enterprise-500">
                              {r.is_default ? 'default ' : ''}
                              {r.is_active === false ? 'inactive' : 'active'}
                            </td>
                          </tr>
                        );
                      })
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-enterprise-600">
            <span>
              Page {page} of {totalPages} ({total} total)
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="inline-flex items-center gap-1 rounded-lg border border-enterprise-200 px-3 py-1.5 font-medium text-enterprise-800 disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" />
                Prev
              </button>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="inline-flex items-center gap-1 rounded-lg border border-enterprise-200 px-3 py-1.5 font-medium text-enterprise-800 disabled:opacity-40"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
